import ProfanityFilter from '../utils/ProfanityFilter.js';
import GameWindow from '../ui/GameWindow.js';

/**
 * PlayerChat — Multiplayer P2P chat window.
 *
 * Uses a GameWindow for draggable/resizable chat panel.
 * Uses the 'chat' data channel via NetworkManager for real-time messaging.
 * Typewriter animation on incoming remote messages (no sound).
 * Toggle button with unread badge, scroll-to-bottom button.
 *
 * DOM structure:
 *   .player-chat-toggle          (collapsed: icon + unread badge)
 *   GameWindow (id: 'chat')      (expanded: draggable/resizable window)
 *     .player-chat-messages      (scrollable message area)
 *       .player-chat-msg         (individual messages)
 *       .player-chat-msg--system (join/leave notifications)
 *     .player-chat-scroll-btn    (scroll-to-bottom FAB)
 *     .player-chat-input-row     (text input + send button)
 */
export default class PlayerChat {
  static MAX_MESSAGES = 100;
  static MAX_MSG_LENGTH = 300;
  static RATE_LIMIT_MS = 500;
  static TYPEWRITER_SPEED_MS = 30;

  // Distinct colors readable on dark backgrounds.
  // No green or blue — reserved for local player (green) and future system/admin.
  static PEER_COLORS = [
    '#f0a030', // amber
    '#e06060', // soft red
    '#a080e0', // lavender
    '#e07080', // rose
    '#c0a060', // sand
    '#e0a0c0', // pink
    '#d08040', // copper
    '#c070d0', // orchid
  ];

  static LOCAL_COLOR = '#28b482'; // green-teal — matches chatbot user bubble

  constructor() {
    this._networkManager = null;
    this._localName = 'player';
    this._messages = [];     // { type, name, text, color }
    this._expanded = false;
    this._visible = false;
    this._unreadCount = 0;
    this._scrollUnread = 0;  // unread while scrolled up
    this._lastSendTime = 0;

    // Typewriter state
    this._twTimer = null;
    this._twEl = null;        // the text span being animated
    this._twText = '';
    this._twIndex = 0;

    // DOM refs
    this._toggleEl = null;
    this._badgeEl = null;
    this._gameWindow = null;
    this._messagesEl = null;
    this._inputEl = null;
    this._sendBtn = null;
    this._scrollBtn = null;
    this._scrollBadgeEl = null;

    // Callback when local player sends a message — used for in-world chat bubbles
    this.onLocalSend = null;

    this._onGlobalKeyDown = this._handleGlobalKeyDown.bind(this);

    this._createElement();
  }

  // ====== Public API ======

  setNetworkManager(nm) {
    this._networkManager = nm;
  }

  setLocalPlayerName(name) {
    this._localName = name || 'player';
  }

  show() {
    this._visible = true;
    this._toggleEl.style.display = 'flex';
    document.addEventListener('keydown', this._onGlobalKeyDown, true);
  }

  hide() {
    this._visible = false;
    this._collapse();
    this._toggleEl.style.display = 'none';
    document.removeEventListener('keydown', this._onGlobalKeyDown, true);
  }

  destroy() {
    this.hide();
    this._finishTypewriter();
    this._toggleEl?.remove();
    if (this._gameWindow) {
      this._gameWindow.destroy();
      this._gameWindow = null;
    }
    this._toggleEl = null;
    this._messagesEl = null;
    this._messages = [];
  }

  /**
   * Handle an incoming remote chat message from the network.
   * @param {Object} data - { name, msg, ts }
   * @param {string} peerId
   */
  addRemoteMessage(data, peerId) {
    if (!data?.msg || typeof data.msg !== 'string') return;
    const name = ProfanityFilter.filter(this._titleCase(data.name || 'player'));
    const text = ProfanityFilter.filter(data.msg.slice(0, PlayerChat.MAX_MSG_LENGTH));
    const color = this._getPeerColor(peerId);
    this._appendMessage('remote', name, text, color);
  }

  /**
   * Add a system message (join/leave notifications).
   * @param {string} text
   */
  addSystemMessage(text) {
    this._appendMessage('system', '', ProfanityFilter.filter(text), '');
  }

  // ====== DOM Creation ======

  _createElement() {
    // Toggle button (stays as fixed element on body)
    this._toggleEl = document.createElement('button');
    this._toggleEl.className = 'player-chat-toggle';
    this._toggleEl.style.display = 'none';
    this._toggleEl.innerHTML = '\u{1F4AC}'; // speech bubble emoji
    this._toggleEl.title = 'Chat (Enter)';
    this._toggleEl.addEventListener('click', () => this._toggle());

    this._badgeEl = document.createElement('span');
    this._badgeEl.className = 'player-chat-badge';
    this._toggleEl.appendChild(this._badgeEl);

    document.body.appendChild(this._toggleEl);

    // Build chat content (messages + input) for the GameWindow
    const chatContent = document.createElement('div');
    chatContent.className = 'player-chat-content';

    // Messages area
    this._messagesEl = document.createElement('div');
    this._messagesEl.className = 'player-chat-messages';
    this._messagesEl.addEventListener('scroll', () => this._checkScrollPosition());

    // Scroll-to-bottom button
    this._scrollBtn = document.createElement('button');
    this._scrollBtn.className = 'player-chat-scroll-btn';
    this._scrollBtn.textContent = '\u25BC'; // ▼
    this._scrollBtn.title = 'Scroll to bottom';
    this._scrollBtn.addEventListener('click', () => {
      this._scrollToBottom(true);
      this._scrollUnread = 0;
      this._updateScrollBadge();
    });

    this._scrollBadgeEl = document.createElement('span');
    this._scrollBadgeEl.className = 'player-chat-scroll-badge';
    this._scrollBtn.appendChild(this._scrollBadgeEl);

    // Input row
    const inputRow = document.createElement('div');
    inputRow.className = 'player-chat-input-row';

    this._inputEl = document.createElement('input');
    this._inputEl.className = 'player-chat-input';
    this._inputEl.type = 'text';
    this._inputEl.placeholder = 'Tap here to type...';
    this._inputEl.maxLength = PlayerChat.MAX_MSG_LENGTH;
    this._inputEl.autocomplete = 'off';
    this._inputEl.addEventListener('keydown', (e) => this._handleInputKeyDown(e));

    this._sendBtn = document.createElement('button');
    this._sendBtn.className = 'player-chat-send';
    this._sendBtn.textContent = 'Send';
    this._sendBtn.addEventListener('click', () => this._handleSend());

    inputRow.appendChild(this._inputEl);
    inputRow.appendChild(this._sendBtn);

    chatContent.appendChild(this._messagesEl);
    chatContent.appendChild(this._scrollBtn);
    chatContent.appendChild(inputRow);

    // Create GameWindow for chat
    const container = document.getElementById('container') || document.body;
    this._gameWindow = new GameWindow({
      container,
      id: 'chat',
      title: 'Chat',
      width: 360,
      height: 320,
      minWidth: 260,
      minHeight: 200,
      x: 16,
      y: container.clientHeight - 336,
      icon: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
      onClose: () => this._collapse(),
      onShow: () => {
        this._expanded = true;
        this._toggleEl.style.display = 'none';
        this._unreadCount = 0;
        this._updateBadge();
      },
      onHide: () => {
        this._expanded = false;
        if (this._visible) this._toggleEl.style.display = 'flex';
        this._inputEl?.blur();
      },
    });

    // Inject chat content into the GameWindow's content area
    const contentContainer = this._gameWindow.getContentContainer();
    contentContainer.style.padding = '0';
    contentContainer.style.display = 'flex';
    contentContainer.style.flexDirection = 'column';
    contentContainer.appendChild(chatContent);
  }

  // ====== Expand / Collapse ======

  _toggle() {
    if (this._expanded) {
      this._collapse();
    } else {
      this._expand();
    }
  }

  _expand() {
    this._gameWindow.show();
    // Do NOT auto-focus input — on mobile this opens the keyboard.
    // User taps the input field explicitly to type.
  }

  _collapse() {
    if (this._gameWindow?.isVisible()) {
      this._gameWindow.hide();
    } else {
      // Ensure state is clean even if window wasn't visible
      this._expanded = false;
      if (this._visible) this._toggleEl.style.display = 'flex';
      this._inputEl?.blur();
    }
  }

  // ====== Sending ======

  _handleSend() {
    const text = this._inputEl.value.trim();
    if (!text) return;

    // Rate limit
    const now = Date.now();
    if (now - this._lastSendTime < PlayerChat.RATE_LIMIT_MS) return;
    this._lastSendTime = now;

    // Send over network
    const msg = {
      name: this._localName,
      msg: text.slice(0, PlayerChat.MAX_MSG_LENGTH),
      ts: now,
    };
    this._networkManager?.broadcastChat(msg);

    // Show locally (instant, no typewriter)
    const filteredText = ProfanityFilter.filter(msg.msg);
    const name = this._titleCase(this._localName);
    this._appendMessage('local', name, filteredText, PlayerChat.LOCAL_COLOR);

    // Trigger in-world chat bubble
    this.onLocalSend?.(filteredText);

    this._inputEl.value = '';
    this._inputEl.focus();
  }

  // ====== Keyboard Handling ======

  /**
   * Global keydown — Enter opens chat when not typing in another input.
   * Captured at the capture phase so it fires before InputManager.
   */
  _handleGlobalKeyDown(e) {
    if (e.key !== 'Enter') return;
    if (!this._visible) return;

    // Don't hijack Enter if already typing in some other input or dialog is active
    const el = document.activeElement;
    const isInOtherInput = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
      && el !== this._inputEl;
    if (isInOtherInput) return;
    if (window.game?.dialogManager?.isActive) return;

    // If chat is collapsed, open it
    if (!this._expanded) {
      e.preventDefault();
      e.stopPropagation();
      this._expand();
      return;
    }

    // If chat is expanded but input not focused, focus it
    if (document.activeElement !== this._inputEl) {
      e.preventDefault();
      e.stopPropagation();
      this._inputEl?.focus();
    }
  }

  /**
   * Keydown on the chat input specifically.
   */
  _handleInputKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      this._handleSend();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this._collapse();
    }
    // Stop all key events from reaching the game when typing in chat
    e.stopPropagation();
  }

  // ====== Message Rendering ======

  _appendMessage(type, name, text, color) {
    this._messages.push({ type, name, text, color });
    this._pruneOldMessages();

    if (type === 'system') {
      const el = document.createElement('div');
      el.className = 'player-chat-msg player-chat-msg--system';
      el.textContent = text;
      this._messagesEl.appendChild(el);
      this._autoScroll();
    } else if (type === 'local') {
      // Local messages: instant, right-aligned green bubble
      this._createMessageEl(name, text, color, 'local');
      this._autoScroll();
    } else {
      // Remote messages: typewriter animation
      this._finishTypewriter(); // finish any in-progress typewriter
      this._appendMessageTypewriter(name, text, color);
    }

    // Update unread counts
    if (!this._expanded) {
      this._unreadCount++;
      this._updateBadge();
    }
    if (!this._isNearBottom()) {
      this._scrollUnread++;
      this._updateScrollBadge();
    }
  }

  _createMessageEl(name, text, color, type = 'remote') {
    const el = document.createElement('div');
    el.className = type === 'local'
      ? 'player-chat-msg player-chat-msg--local'
      : 'player-chat-msg player-chat-msg--remote';

    // Set peer color CSS custom properties for remote bubble styling
    if (type === 'remote' && color) {
      el.style.setProperty('--peer-border', color);
      // Parse hex to rgba for semi-transparent background
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      el.style.setProperty('--peer-bg', `rgba(${r}, ${g}, ${b}, 0.12)`);
      el.style.setProperty('--peer-fill', `rgba(${Math.round(r * 0.15)}, ${Math.round(g * 0.15)}, ${Math.round(b * 0.15)}, 0.92)`);
    }

    const nameSpan = document.createElement('span');
    nameSpan.className = 'player-chat-msg-name';
    nameSpan.textContent = name + ':';
    nameSpan.style.color = color;

    const textSpan = document.createElement('span');
    textSpan.textContent = text;

    el.appendChild(nameSpan);
    el.appendChild(textSpan);
    this._messagesEl.appendChild(el);

    return textSpan;
  }

  // ====== Typewriter Animation ======

  _appendMessageTypewriter(name, text, color) {
    const textSpan = this._createMessageEl(name, '', color);
    this._twEl = textSpan;
    this._twText = text;
    this._twIndex = 0;
    this._typewriterNext();
  }

  _typewriterNext() {
    if (!this._twEl || this._twIndex >= this._twText.length) {
      this._twEl = null;
      this._twText = '';
      this._twIndex = 0;
      return;
    }
    this._twIndex++;
    this._twEl.textContent = this._twText.substring(0, this._twIndex);
    this._autoScroll();
    this._twTimer = setTimeout(() => this._typewriterNext(), PlayerChat.TYPEWRITER_SPEED_MS);
  }

  _finishTypewriter() {
    if (this._twTimer) {
      clearTimeout(this._twTimer);
      this._twTimer = null;
    }
    if (this._twEl && this._twText) {
      this._twEl.textContent = this._twText;
    }
    this._twEl = null;
    this._twText = '';
    this._twIndex = 0;
  }

  // ====== Scroll Management ======

  _isNearBottom() {
    if (!this._messagesEl) return true;
    const { scrollTop, scrollHeight, clientHeight } = this._messagesEl;
    return scrollTop + clientHeight >= scrollHeight - 40;
  }

  _autoScroll() {
    if (this._isNearBottom()) {
      this._scrollToBottom(false);
    }
  }

  _scrollToBottom(smooth) {
    if (!this._messagesEl) return;
    requestAnimationFrame(() => {
      this._messagesEl.scrollTo({
        top: this._messagesEl.scrollHeight,
        behavior: smooth ? 'smooth' : 'instant',
      });
    });
  }

  _checkScrollPosition() {
    if (this._isNearBottom()) {
      this._scrollBtn.classList.remove('visible');
      this._scrollUnread = 0;
      this._updateScrollBadge();
    } else {
      this._scrollBtn.classList.add('visible');
    }
  }

  // ====== Badge Updates ======

  _updateBadge() {
    if (this._unreadCount > 0) {
      this._badgeEl.textContent = this._unreadCount > 9 ? '9+' : String(this._unreadCount);
      this._badgeEl.classList.add('visible');
    } else {
      this._badgeEl.classList.remove('visible');
    }
  }

  _updateScrollBadge() {
    if (this._scrollUnread > 0) {
      this._scrollBadgeEl.textContent = this._scrollUnread > 9 ? '9+' : String(this._scrollUnread);
      this._scrollBadgeEl.classList.add('visible');
    } else {
      this._scrollBadgeEl.classList.remove('visible');
    }
  }

  // ====== Utilities ======

  _pruneOldMessages() {
    while (this._messages.length > PlayerChat.MAX_MESSAGES) {
      this._messages.shift();
      // Remove oldest DOM child
      if (this._messagesEl?.firstChild) {
        this._messagesEl.removeChild(this._messagesEl.firstChild);
      }
    }
  }

  _getPeerColor(peerId) {
    if (!peerId) return PlayerChat.PEER_COLORS[0];
    let hash = 0;
    for (let i = 0; i < peerId.length; i++) {
      hash = ((hash << 5) - hash + peerId.charCodeAt(i)) | 0;
    }
    return PlayerChat.PEER_COLORS[Math.abs(hash) % PlayerChat.PEER_COLORS.length];
  }

  _titleCase(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
